"use client"

/**
 * Client-side error capture.
 *
 * Posts to /api/telemetry instead of Axiom directly so:
 *   - The Axiom ingest token stays server-only.
 *   - The user identity is server-stamped (client cannot forge userId/role).
 *   - CSP connect-src does not need to allow api.axiom.co from the browser.
 *
 * Rate-limited in memory to 20 events / 60s per page load so a render loop
 * cannot DDoS our own /api/telemetry route. Exceeding the budget logs a
 * one-line console warning and drops the excess.
 */

export interface ClientErrorContext {
  route?: string
  boundary?: string
  digest?: string
  [key: string]: string | number | boolean | undefined
}

const WINDOW_MS = 60_000
const MAX_PER_WINDOW = 20

let recent: number[] = []

function withinBudget(): boolean {
  const now = Date.now()
  recent = recent.filter((t) => now - t < WINDOW_MS)
  if (recent.length >= MAX_PER_WINDOW) return false
  recent.push(now)
  return true
}

function buildPayload(err: Error, ctx: ClientErrorContext) {
  return {
    message: err.message,
    name: err.name,
    stack: err.stack ?? null,
    context: {
      ...ctx,
      route: ctx.route ?? (typeof window !== "undefined" ? window.location.pathname : undefined),
      appVersion: process.env.NEXT_PUBLIC_APP_VERSION ?? "dev",
      deploymentMode: process.env.NEXT_PUBLIC_DEPLOYMENT_MODE ?? "cloud",
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : undefined,
    },
  }
}

/**
 * Report a client-side error. Fire-and-forget. Never throws.
 * Always falls through to console.error so developers see it in the
 * browser console too.
 */
export function captureException(err: unknown, ctx: ClientErrorContext = {}): void {
  const error = err instanceof Error ? err : new Error(String(err))

  // Always log to console — the server-side ingest can fail silently.
  console.error("[telemetry]", error, ctx)

  if (!withinBudget()) {
    console.warn("[telemetry] rate limit hit; dropping client error")
    return
  }

  const payload = buildPayload(error, ctx)

  try {
    // Prefer sendBeacon for pagehide/unload reliability; fall back to fetch.
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      const blob = new Blob([JSON.stringify(payload)], { type: "application/json" })
      const delivered = navigator.sendBeacon("/api/telemetry", blob)
      if (delivered) return
    }
    void fetch("/api/telemetry", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {
      // Silent — we already logged to console.
    })
  } catch {
    // Swallow. Telemetry must never crash the app.
  }
}
